import { TypedAliasData } from "../../../../laravel";
import { AliasFactory, Alias } from "./AliasFactory.ts";
import { Tag } from "./TagModel.ts";

export interface UrlAlias extends Omit<TypedAliasData, "aliases"> {
    aliases: Alias[],
    setAliases(aliases: Alias[]): UrlAlias,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- query bag mirrors the untyped router visit payload
    setVisit(newVisit: (alias: Alias, query?: Record<string, any>) => void): UrlAlias,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- query bag mirrors the untyped router visit payload
    setTagVisit(callback: (tag: Tag, query?: Record<string, any>) => void): UrlAlias,
    visit(): void,
    mainAlias(): Alias | null,
}

/**
 * Takes TypedAliasData from the server and converts it into an object with methods that we can use to interact
 * with it.
 */
export default function UrlAliasFactory(data: TypedAliasData): UrlAlias {
    const that = {
        ...data,

        /** Convert the list of aliases into Alias models */
        aliases: Object.values(data.aliases).map((aliasData) => AliasFactory(aliasData)),

        /**
         * Set the route that should be called when visit() is called on the aliases of this class
         * This closure will be set on every alias in the array
         */
        setVisit(callback = (alias: Alias, query = {}) => {}): UrlAlias {
            Object.values(that.aliases).forEach((alias: Alias) => {
                alias.setVisit(callback);
            });

            return that;
        },

        /**
         * Set the route that should be called when visit() is called on the tags of this class
         * This closure will be set on every tag in every alias
         */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- query bag mirrors the untyped router visit payload
        setTagVisit(callback: (tag: Tag, query?: Record<string, any>) => void): UrlAlias {
            Object.values(that.aliases).forEach((alias: Alias) => {
                alias.setTagVisit(callback);
            });

            return that;
        },

        /**
         * Redirect the browser to the details page for this alias
         */
        visit() {
            that.mainAlias()?.visit();
        },

        /**
         * Returns the 'main' alias for this url
         */ 
        mainAlias() {
            return that.aliases?.find((aliasInfo) => aliasInfo.is_main) || null;
        },

        /**
         * Allows you to override the list of aliases with another set
         */
        setAliases(aliases: Alias[]): UrlAlias {
            that.aliases = aliases;
            return that;
        }
    };

    return that;
}
